import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect';

let lockCount = 0;

let previousStyles: {
  htmlOverflow: string;
  bodyOverflow: string;
  bodyPaddingRight: string;
} | null = null;

const touchListenerOptions: AddEventListenerOptions = { passive: false };

const setStyle = (
  element: HTMLElement,
  property: 'overflow' | 'padding-right',
  value: string
) => {
  if (value === '') {
    element.style.removeProperty(property);
  } else {
    element.style.setProperty(property, value);
  }
};

const isTouchDevice = () =>
  typeof window !== 'undefined' &&
  ('ontouchstart' in window || navigator.maxTouchPoints > 0);

const isScrollable = (element: HTMLElement) => {
  const { overflowY, overflowX } = window.getComputedStyle(element);
  const scrollsY =
    /(auto|scroll)/.test(overflowY) &&
    element.scrollHeight > element.clientHeight;
  const scrollsX =
    /(auto|scroll)/.test(overflowX) &&
    element.scrollWidth > element.clientWidth;

  return scrollsY || scrollsX;
};

const findScrollableAncestor = (target: EventTarget | null) => {
  let element = target instanceof Element ? target : null;

  while (
    element &&
    element !== document.body &&
    element !== document.documentElement
  ) {
    if (element instanceof HTMLElement && isScrollable(element)) {
      return element;
    }

    element = element.parentElement;
  }

  return null;
};

const preventTouchMove = (event: TouchEvent) => {
  if (!findScrollableAncestor(event.target)) {
    event.preventDefault();
  }
};

const preventInertiaScroll = (event: TouchEvent) => {
  const scroller = findScrollableAncestor(event.target);

  if (!scroller) {
    return;
  }

  const { scrollTop, scrollHeight, offsetHeight } = scroller;

  if (scrollTop <= 0) {
    scroller.scrollTop = 1;
  } else if (scrollTop + offsetHeight >= scrollHeight) {
    scroller.scrollTop = scrollTop - 1;
  }
};

const addTouchListeners = () => {
  document.addEventListener(
    'touchstart',
    preventInertiaScroll,
    touchListenerOptions
  );
  document.addEventListener(
    'touchmove',
    preventTouchMove,
    touchListenerOptions
  );
};

const removeTouchListeners = () => {
  document.removeEventListener(
    'touchstart',
    preventInertiaScroll,
    touchListenerOptions
  );
  document.removeEventListener(
    'touchmove',
    preventTouchMove,
    touchListenerOptions
  );
};

/**
 * Locks page scrolling while `locked` is true, compensating for the width of
 * the scrollbar that disappears so the page doesn't shift.
 *
 * iOS Safari ignores `overflow: hidden` for touch scrolling, so on touch
 * devices `touchmove` is additionally cancelled unless it happens inside an
 * element that can scroll on its own (e.g. a drawer's content area).
 */
export const useScrollLock = (locked: boolean) => {
  useIsomorphicLayoutEffect(() => {
    if (!locked || typeof document === 'undefined') {
      return;
    }

    const { body, documentElement: html } = document;

    if (lockCount === 0) {
      previousStyles = {
        htmlOverflow: html.style.overflow,
        bodyOverflow: body.style.overflow,
        bodyPaddingRight: body.style.paddingRight
      };

      const scrollbarWidth = window.innerWidth - html.clientWidth;

      if (scrollbarWidth > 0) {
        const currentPaddingRight =
          parseFloat(window.getComputedStyle(body).paddingRight) || 0;

        setStyle(
          body,
          'padding-right',
          `${currentPaddingRight + scrollbarWidth}px`
        );
      }

      setStyle(html, 'overflow', 'hidden');
      setStyle(body, 'overflow', 'hidden');

      if (isTouchDevice()) {
        addTouchListeners();
      }
    }

    lockCount += 1;

    return () => {
      lockCount = Math.max(0, lockCount - 1);

      if (lockCount === 0 && previousStyles !== null) {
        setStyle(html, 'overflow', previousStyles.htmlOverflow);
        setStyle(body, 'overflow', previousStyles.bodyOverflow);
        setStyle(body, 'padding-right', previousStyles.bodyPaddingRight);
        previousStyles = null;
        removeTouchListeners();
      }
    };
  }, [locked]);
};
